import React, { useState, useRef } from 'react';

const CircularQueueGame = () => {
  const [queue, setQueue] = useState(Array(6).fill(null));
  const [selectedFront, setSelectedFront] = useState(-1);
  const [selectedRear, setSelectedRear] = useState(-1);
  const [currentStep, setCurrentStep] = useState(1);
  const [status, setStatus] = useState({ message: '', type: '' });
  const [gameCompleted, setGameCompleted] = useState(false);
  const [draggedShip, setDraggedShip] = useState(null);
  const [isValidDrop, setIsValidDrop] = useState(false);

  const QUEUE_SIZE = 6;

  // Προκαθορισμένα σκάφη με μοτίβο
  const availableShips = [
    { id: 'A', name: 'APOLLO', color: '#ff6b6b', emoji: '🚀' },
    { id: 'B', name: 'BETA', color: '#4ecdc4', emoji: '🛸' },
    { id: 'C', name: 'COSMOS', color: '#45b7d1', emoji: '🚁' },
    { id: 'D', name: 'DELTA', color: '#96ceb4', emoji: '✈️' },
    { id: 'E', name: 'ECHO', color: '#feca57', emoji: '🛩️' }
  ];

  // Το σωστό μοτίβο που πρέπει να ακολουθήσουν
  const correctPattern = [
    { step: 1, action: 'enqueue', ship: 'A', position: 0, hint: 'Αρχικό σκάφος αναγνώρισης' },
    { step: 2, action: 'enqueue', ship: 'B', position: 1, hint: 'Δεύτερο σκάφος περιπολίας' },
    { step: 3, action: 'enqueue', ship: 'C', position: 2, hint: 'Τρίτο σκάφος εξερεύνησης' },
    { step: 4, action: 'enqueue', ship: 'D', position: 3, hint: 'Τέταρτο σκάφος επιθεώρησης' },
    { step: 5, action: 'dequeue', ship: 'A', position: null, hint: 'Το πρώτο σκάφος ολοκλήρωσε την αποστολή' },
    { step: 6, action: 'enqueue', ship: 'E', position: 4, hint: 'Νέο σκάφος έκτακτης ανάγκης' },
    { step: 7, action: 'dequeue', ship: 'B', position: null, hint: 'Το δεύτερο σκάφος επιστρέφει στη βάση' },
    { step: 8, action: 'enqueue', ship: 'A', position: 0, hint: 'Το πρώτο σκάφος επιστρέφει με καύσιμα' }
  ];

  const [currentPatternStep, setCurrentPatternStep] = useState(0);
  const [usedShips, setUsedShips] = useState([]);

  // Δημιουργία αστεριών
  const createStars = () => {
    return Array.from({ length: 100 }, (_, i) => (
      <div
        key={i}
        style={{
          position: 'absolute',
          width: '2px',
          height: '2px',
          background: 'white',
          borderRadius: '50%',
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          opacity: Math.random() * 0.8 + 0.2,
          animation: `twinkle ${Math.random() * 2 + 1}s infinite alternate`,
          animationDelay: `${Math.random() * 2}s`
        }}
      />
    ));
  };

  const showStatus = (message, type) => {
    setStatus({ message, type });
    setTimeout(() => setStatus({ message: '', type: '' }), 3000);
  };

  const getCurrentInstruction = () => {
    if (currentPatternStep >= correctPattern.length) {
      return "🎯 Τελική φάση: Προσδιόρισε τους δείκτες FRONT και REAR της ουράς!";
    }
    const current = correctPattern[currentPatternStep];

    // Διαφορετικές οδηγίες για κάθε βήμα
    switch (currentPatternStep) {
      case 0:
        return `🚀 ${current.hint} - Τοποθέτησέ το στη θέση 0`;
      case 1:
        return `🛸 ${current.hint} - Τοποθέτησέ το στην επόμενη διαθέσιμη θέση`;
      case 2:
        return `🚁 ${current.hint} - Συνέχισε την ακολουθία FIFO`;
      case 3:
        return `✈️ ${current.hint} - Τοποθέτησέ το στην τελευταία ελεύθερη θέση`;
      case 4:
        return `🛸 ${current.hint} - Κάνε κλικ στο σκάφος A για αναχώρηση`;
      case 5:
        return `🛩️ ${current.hint} - Χρησιμοποίησε την κυκλική λογική (θέση 0 είναι ελεύθερη!)`;
      case 6:
        return `🛸 ${current.hint} - Κάνε κλικ στο σκάφος B για αναχώρηση`;
      case 7:
        return `🚀 ${current.hint} - Το A επιστρέφει στη θέση 1`;
      default:
        return `📍 ${current.hint} - Ακολούθησε την FIFO λογική`;
    }
  };

  const getFront = () => {
    for (let i = 0; i < QUEUE_SIZE; i++) {
      if (queue[i] !== null) return i;
    }
    return -1;
  };

  const getRear = () => {
    for (let i = QUEUE_SIZE - 1; i >= 0; i--) {
      if (queue[i] !== null) return i;
    }
    return -1;
  };

  const handleDragStart = (e, ship) => {
    setDraggedShip(ship);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();

    if (!draggedShip || currentPatternStep >= correctPattern.length) return;

    const current = correctPattern[currentPatternStep];

    // Έλεγχος αν το drag είναι έγκυρο
    if (current.action === 'enqueue' &&
      current.ship === draggedShip.id &&
      current.position === index &&
      queue[index] === null) {
      setIsValidDrop(true);
      e.dataTransfer.dropEffect = 'move';
    } else {
      setIsValidDrop(false);
      e.dataTransfer.dropEffect = 'none';
    }
  };

  const handleDragLeave = () => {
    setIsValidDrop(false);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();

    if (!draggedShip || currentPatternStep >= correctPattern.length) return;

    const current = correctPattern[currentPatternStep];

    if (current.action === 'enqueue' &&
      current.ship === draggedShip.id &&
      current.position === index &&
      queue[index] === null) {

      const newQueue = [...queue];
      newQueue[index] = draggedShip;
      setQueue(newQueue);
      setUsedShips([...usedShips, draggedShip.id]);
      setCurrentPatternStep(currentPatternStep + 1);

      showStatus(`Σκάφος ${draggedShip.name} προσγειώθηκε επιτυχώς! 🚀`, 'success');

      if (currentPatternStep + 1 >= correctPattern.length) {
        setCurrentStep(2);
        setTimeout(() => {
          showStatus('Τώρα επιλέξτε τους σωστούς δείκτες FRONT και REAR!', 'info');
        }, 1500);
      }
    } else {
      showStatus('Λάθος θέση! Ακολούθησε τις οδηγίες.', 'error');
    }

    setDraggedShip(null);
    setIsValidDrop(false);
  };

  const handleSlotClick = (index) => {
    if (currentStep === 1 && queue[index] !== null && currentPatternStep < correctPattern.length) {
      const current = correctPattern[currentPatternStep];

      if (current.action === 'dequeue' && queue[index].id === current.ship) {
        const newQueue = [...queue];
        const removedShip = newQueue[index];
        newQueue[index] = null;
        setQueue(newQueue);
        setCurrentPatternStep(currentPatternStep + 1);

        showStatus(`Σκάφος ${removedShip.name} αναχώρησε! 🛸`, 'success');

        if (currentPatternStep + 1 >= correctPattern.length) {
          setCurrentStep(2);
          setTimeout(() => {
            showStatus('Τώρα επιλέξτε τους σωστούς δείκτες FRONT και REAR!', 'info');
          }, 1500);
        }
      } else {
        showStatus('Λάθος σκάφος για αφαίρεση!', 'error');
      }
    } else if (currentStep === 2) {
      selectPointer(index);
    }
  };

  const selectPointer = (index) => {
    if (currentStep !== 2) return;

    if (selectedFront === index) {
      setSelectedFront(-1);
      showStatus('Front αποεπιλέχθηκε.', 'info');
    } else if (selectedRear === index) {
      setSelectedRear(-1);
      showStatus('Rear αποεπιλέχθηκε.', 'info');
    } else {
      if (selectedFront === -1) {
        setSelectedFront(index);
        showStatus('Front επιλέχθηκε! Τώρα επιλέξτε το Rear.', 'info');
      } else if (selectedRear === -1 && index !== selectedFront) {
        setSelectedRear(index);
        showStatus('Rear επιλέχθηκε! Πατήστε "Ολοκλήρωση Αποστολής".', 'success');
      }
    }
  };

  const checkAnswer = () => {
    if (selectedFront === -1 || selectedRear === -1) {
      showStatus('Επιλέξτε και τους δύο δείκτες!', 'error');
      return;
    }

    const correctFront = getFront();
    const correctRear = getRear();

    if (selectedFront === correctFront && selectedRear === correctRear) {
      showStatus('🎉 ΕΠΙΤΥΧΙΑ! Ολοκληρώσατε την αποστολή με επιτυχία!', 'success');
      setGameCompleted(true);
    } else {
      showStatus(`❌ Λάθος! Σωστό Front: ${correctFront >= 0 ? correctFront : 'κενό'}, Σωστό Rear: ${correctRear >= 0 ? correctRear : 'κενό'}`, 'error');
    }
  };

  const resetGame = () => {
    setQueue(Array(6).fill(null));
    setSelectedFront(-1);
    setSelectedRear(-1);
    setCurrentStep(1);
    setStatus({ message: '', type: '' });
    setGameCompleted(false);
    setCurrentPatternStep(0);
    setUsedShips([]);
    setDraggedShip(null);
    setIsValidDrop(false);
  };

  const getSlotStyle = (index) => {
    const radius = 150;
    const angle = (index * 360 / QUEUE_SIZE) * Math.PI / 180;
    const x = 200 + radius * Math.cos(angle - Math.PI / 2);
    const y = 200 + radius * Math.sin(angle - Math.PI / 2);

    let backgroundColor = 'rgba(255, 255, 255, 0.05)';
    let borderColor = '#666';
    let boxShadow = 'none';
    let transform = 'none';

    if (queue[index] !== null) {
      backgroundColor = queue[index].color;
      borderColor = queue[index].color;
      boxShadow = `0 0 15px ${queue[index].color}50`;
    }

    if (selectedFront === index) {
      borderColor = '#4ecdc4';
      backgroundColor = queue[index] ? queue[index].color : 'rgba(78, 205, 196, 0.3)';
      boxShadow = '0 0 20px rgba(78, 205, 196, 0.5)';
    }

    if (selectedRear === index) {
      borderColor = '#45b7d1';
      backgroundColor = queue[index] ? queue[index].color : 'rgba(69, 183, 209, 0.3)';
      boxShadow = '0 0 20px rgba(69, 183, 209, 0.5)';
    }

    if (isValidDrop && draggedShip) {
      const current = correctPattern[currentPatternStep];
      if (current?.position === index && current?.action === 'enqueue') {
        borderColor = '#00ff00';
        backgroundColor = 'rgba(0, 255, 0, 0.2)';
        transform = 'scale(1.1)';
      }
    }

    return {
      position: 'absolute',
      left: `${x - 35}px`,
      top: `${y - 35}px`,
      width: '70px',
      height: '70px',
      border: `3px solid ${borderColor}`,
      borderRadius: '15px',
      background: backgroundColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.8em',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      color: queue[index] !== null ? 'white' : '#666',
      fontWeight: queue[index] !== null ? 'bold' : 'normal',
      boxShadow,
      transform,
      zIndex: selectedFront === index || selectedRear === index ? 10 : 1
    };
  };

  const getAvailableShips = () => {
    return availableShips.filter(ship => !usedShips.includes(ship.id));
  };

  return (
    <div style={{
      fontFamily: "'Courier New', monospace",
      background: 'linear-gradient(135deg, #0c0c0c 0%, #1a1a2e 50%, #16213e 100%)',
      minHeight: '100vh',
      color: '#ffffff',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Αστέρια */}
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1 }}>
        <style>{`
          @keyframes twinkle {
            0% { opacity: 0.3; }
            100% { opacity: 1; }
          }
        `}</style>
        {createStars()}
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '30px',
          background: 'rgba(255, 255, 255, 0.1)',
          padding: '20px',
          borderRadius: '15px',
          backdropFilter: 'blur(10px)',
          border: '2px solid #00ffff',
          boxShadow: '0 0 30px rgba(0, 255, 255, 0.3)'
        }}>
          <h1 style={{
            fontSize: '2.5em',
            color: '#00ffff',
            textShadow: '0 0 10px #00ffff',
            marginBottom: '10px',
            margin: 0
          }}>
            🚀 ΔΙΑΣΤΗΜΙΚΟΣ ΣΤΑΘΜΟΣ 🛸
          </h1>
          <p style={{ margin: '10px 0 0 0' }}>Drag & Drop Circular Queue Mission</p>
        </div>

        {/* Mission Brief */}
        <div style={{
          background: 'rgba(255, 165, 0, 0.1)',
          border: '2px solid #ffa500',
          borderRadius: '10px',
          padding: '20px',
          margin: '20px 0',
          textAlign: 'center'
        }}>
          <h2 style={{
            color: '#ffa500',
            fontSize: '1.3em',
            margin: '0 0 15px 0'
          }}>
            📡 ΑΠΟΣΤΟΛΗ: Διαχείριση Διαστημικού Κέντρου Ελέγχου
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '15px',
            margin: '15px 0'
          }}>
            <div style={{
              background: 'rgba(78, 205, 196, 0.1)',
              border: '2px solid #4ecdc4',
              borderRadius: '8px',
              padding: '10px',
              fontSize: '0.9em'
            }}>
              <strong>📋 Κανόνες FIFO:</strong><br />
              Πρώτο που μπαίνει, πρώτο που βγαίνει
            </div>

            <div style={{
              background: 'rgba(69, 183, 209, 0.1)',
              border: '2px solid #45b7d1',
              borderRadius: '8px',
              padding: '10px',
              fontSize: '0.9em'
            }}>
              <strong>🔄 Κυκλική Λογική:</strong><br />
              Μετά την τελευταία θέση → πρώτη θέση
            </div>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            border: '2px solid #fff',
            borderRadius: '10px',
            padding: '15px',
            margin: '15px 0',
            fontSize: '1em',
            color: '#00ffff'
          }}>
            <strong>🎯 Τρέχουσα Αποστολή:</strong><br />
            {getCurrentInstruction()}
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-around',
            flexWrap: 'wrap',
            gap: '10px',
            fontSize: '0.8em',
            color: '#ccc'
          }}>
            <span>💡 Σκέψου την κυκλική δομή</span>
            <span>🧠 Χρησιμοποίησε τη λογική FIFO</span>
            <span>⚡ Παρακολούθησε τους δείκτες</span>
          </div>
        </div>

        {/* Available Ships */}
        {currentStep === 1 && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '2px solid #666',
            borderRadius: '15px',
            padding: '20px',
            margin: '20px 0',
            textAlign: 'center'
          }}>
            <h3 style={{ color: '#ffa500', margin: '0 0 10px 0' }}>🚁 Διαθέσιμα Σκάφη στο Hangar</h3>
            <p style={{ fontSize: '0.8em', color: '#ccc', margin: '5px 0 15px 0' }}>
              Σύρε τα σκάφη στις κατάλληλες θέσεις ή κάνε κλικ για αναχώρηση
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '15px',
              flexWrap: 'wrap'
            }}>
              {getAvailableShips().map(ship => (
                <div
                  key={ship.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, ship)}
                  style={{
                    padding: '12px 20px',
                    background: ship.color,
                    border: `2px solid ${ship.color}`,
                    borderRadius: '20px',
                    cursor: 'grab',
                    transition: 'all 0.3s ease',
                    boxShadow: `0 5px 15px ${ship.color}50`,
                    fontSize: '0.9em',
                    fontWeight: 'bold',
                    color: 'white',
                    userSelect: 'none',
                    position: 'relative'
                  }}
                  onMouseDown={(e) => e.target.style.cursor = 'grabbing'}
                  onMouseUp={(e) => e.target.style.cursor = 'grab'}
                >
                  {ship.emoji} {ship.name}
                  <div style={{
                    position: 'absolute',
                    bottom: '-5px',
                    right: '-5px',
                    background: 'rgba(255,255,255,0.9)',
                    color: 'black',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    fontSize: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}>
                    {ship.id}
                  </div>
                </div>
              ))}
            </div>

            {currentPatternStep > 0 && (
              <div style={{
                marginTop: '15px',
                fontSize: '0.8em',
                color: '#4ecdc4'
              }}>
                💭 Θυμήσου: Η ουρά ακολουθεί την αρχή FIFO (First In, First Out)
              </div>
            )}
          </div>
        )}

        {/* Progress */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '20px 0',
          background: 'rgba(255, 255, 255, 0.05)',
          padding: '15px',
          borderRadius: '10px',
          border: '1px solid #444'
        }}>
          <div style={{ color: '#ffa500', fontSize: '1em' }}>
            📊 Πρόοδος Αποστολής: <strong>{currentPatternStep}/{correctPattern.length}</strong>
          </div>
          <div style={{ color: '#4ecdc4', fontSize: '0.9em' }}>
            {currentStep === 1 ? '🔧 Φάση Διαχείρισης' : '🎯 Φάση Αξιολόγησης'}
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{
          width: '100%',
          height: '8px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
          overflow: 'hidden',
          margin: '20px 0'
        }}>
          <div style={{
            height: '100%',
            background: 'linear-gradient(90deg, #4ecdc4, #45b7d1)',
            transition: 'width 0.5s ease',
            borderRadius: '4px',
            width: `${(currentPatternStep / correctPattern.length) * 100}%`
          }} />
        </div>

        {/* Circular Queue */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '40px 0',
          position: 'relative'
        }}>
          <div style={{
            width: '450px',
            height: '450px',
            border: '3px solid #00ffff',
            borderRadius: '50%',
            position: 'relative',
            background: 'radial-gradient(circle at center, rgba(0, 255, 255, 0.1), transparent)',
            boxShadow: '0 0 50px rgba(0, 255, 255, 0.2)'
          }}>
            {Array.from({ length: QUEUE_SIZE }, (_, i) => (
              <div
                key={i}
                style={getSlotStyle(i)}
                onClick={() => handleSlotClick(i)}
                onDragOver={(e) => handleDragOver(e, i)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, i)}
              >
                <div style={{ textAlign: 'center', fontSize: '0.8em' }}>
                  {queue[i] !== null ? (
                    <>
                      <div style={{ fontSize: '1.2em' }}>{queue[i].emoji}</div>
                      <div>{queue[i].id}</div>
                    </>
                  ) : (
                    <div style={{ color: '#666', fontSize: '0.9em' }}>{i}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '30px',
          margin: '20px 0',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9em' }}>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '5px',
              background: 'rgba(255, 107, 107, 0.3)',
              border: '2px solid #ff6b6b'
            }} />
            <span>Σκάφη</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9em' }}>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '5px',
              background: 'rgba(78, 205, 196, 0.3)',
              border: '2px solid #4ecdc4'
            }} />
            <span>Front Pointer</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9em' }}>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '5px',
              background: 'rgba(69, 183, 209, 0.3)',
              border: '2px solid #45b7d1'
            }} />
            <span>Rear Pointer</span>
          </div>
        </div>

        {/* Controls */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '15px',
          margin: '30px 0'
        }}>
          {currentStep === 2 && !gameCompleted && selectedFront >= 0 && selectedRear >= 0 && (
            <button
              onClick={checkAnswer}
              style={{
                padding: '15px 30px',
                border: '2px solid #ffa500',
                borderRadius: '25px',
                background: 'rgba(255, 165, 0, 0.1)',
                color: '#ffa500',
                cursor: 'pointer',
                fontSize: '1.1em',
                fontWeight: 'bold',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(5px)',
                boxShadow: '0 0 20px rgba(255, 165, 0, 0.3)'
              }}
            >
              ✅ ΟΛΟΚΛΗΡΩΣΗ ΑΠΟΣΤΟΛΗΣ
            </button>
          )}
          <button
            onClick={resetGame}
            style={{
              padding: '15px 30px',
              border: '2px solid #666',
              borderRadius: '25px',
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#ccc',
              cursor: 'pointer',
              fontSize: '1.1em',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(5px)'
            }}
          >
            🔄 ΝΕΑΑ ΑΠΟΣΤΟΛΗ
          </button>
        </div>

        {/* Status */}
        {status.message && (
          <div style={{
            textAlign: 'center',
            padding: '15px',
            margin: '20px 0',
            borderRadius: '10px',
            fontSize: '1.1em',
            background: status.type === 'success' ? 'rgba(78, 205, 196, 0.2)' :
              status.type === 'error' ? 'rgba(255, 107, 107, 0.2)' :
                'rgba(69, 183, 209, 0.2)',
            border: status.type === 'success' ? '2px solid #4ecdc4' :
              status.type === 'error' ? '2px solid #ff6b6b' :
                '2px solid #45b7d1',
            color: status.type === 'success' ? '#4ecdc4' :
              status.type === 'error' ? '#ff6b6b' :
                '#45b7d1'
          }}>
            {status.message}
          </div>
        )}

        {/* Debug Info */}
        <div style={{
          textAlign: 'center',
          marginTop: '30px',
          fontSize: '0.8em',
          color: '#666',
          background: 'rgba(255, 255, 255, 0.05)',
          padding: '15px',
          borderRadius: '10px',
          border: '1px solid #333'
        }}>
          <div style={{ marginBottom: '8px', color: '#888' }}>
            🔍 <strong>Κατάσταση Συστήματος:</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '15px' }}>
            <span>Front Index: <strong style={{ color: '#4ecdc4' }}>{getFront() >= 0 ? getFront() : 'κενό'}</strong></span>
            <span>Rear Index: <strong style={{ color: '#45b7d1' }}>{getRear() >= 0 ? getRear() : 'κενό'}</strong></span>
            <span>Ενεργά Σκάφη: <strong style={{ color: '#ffa500' }}>{queue.filter(s => s !== null).length}</strong></span>
          </div>
          <div style={{ marginTop: '8px', fontSize: '0.7em', color: '#555' }}>
            Αυτές οι πληροφορίες θα σε βοηθήσουν στην τελική επιλογή των δεικτών
          </div>
        </div>
      </div>
    </div>
  );
};

export default CircularQueueGame;